import { useState } from "react";
import { api, formatNum } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const fromRecipe = (recipe) => recipe.ingredients.map((i) => ({ name: i.flavor ? `${i.name} (${i.flavor})` : i.name, amount: `${formatNum(i.grams, i.grams % 1 ? 2 : 0)} ${i.unit || "g"}`, measure: i.measure || "" }));

export const GuideEditor = ({ recipe, onClose, onSaved }) => {
  const g = recipe.guide || {};
  const [steps, setSteps] = useState((g.steps || []).join("\n"));
  const [prep, setPrep] = useState(g.prep_minutes || "");
  const [tip, setTip] = useState(g.tip || "");
  const [custom, setCustom] = useState(!!g.custom_ingredients);
  const [ings, setIngs] = useState(g.custom_ingredients && g.ingredients?.length ? g.ingredients : fromRecipe(recipe));
  const [saving, setSaving] = useState(false);
  const upd = (i, patch) => setIngs(ings.map((x, k) => (k === i ? { ...x, ...patch } : x)));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/recipes/${recipe.id}/guide`, {
        steps: steps.split("\n").map((s) => s.replace(/^\s*\d+[.)]\s*/, "").trim()).filter(Boolean), prep_minutes: parseInt(prep, 10) || 0, tip,
        custom_ingredients: custom, ingredients: custom ? ings : [],
      });
      toast.success("Hazırlanış kaydedildi"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Kaydedilemedi"); }
    setSaving(false);
  };

  return (
    <div data-testid="guide-editor">
      <p className="text-xs font-semibold uppercase tracking-wider text-primary">{recipe.category}</p>
      <h2 className="font-heading text-2xl font-extrabold">{recipe.display_name} — Hazırlanış</h2>
      <div className="mt-5 space-y-4">
        <div className="rounded-lg border border-border p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <Label className="text-xs">Malzemeler</Label>
              <p className="text-[11px] text-muted-foreground">{custom ? "Tarife özel liste — ürün reçetesini ve stoğu etkilemez" : "Ürün reçetesinden otomatik alınır"}</p>
            </div>
            <label className="flex items-center gap-2 text-xs font-semibold"><Switch checked={custom} onCheckedChange={setCustom} data-testid="guide-custom-ings" /> Özel liste</label>
          </div>
          {custom && (
            <div className="mt-3 space-y-1.5" data-testid="guide-ing-rows">
              {ings.map((it, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <Input value={it.name} onChange={(e) => upd(i, { name: e.target.value })} placeholder="Malzeme" className="h-8 flex-1 text-sm" data-testid={`guide-ing-name-${i}`} />
                  <Input value={it.amount} onChange={(e) => upd(i, { amount: e.target.value })} placeholder="Miktar" className="h-8 w-24 text-sm" data-testid={`guide-ing-amount-${i}`} />
                  <Input value={it.measure} onChange={(e) => upd(i, { measure: e.target.value })} placeholder="Ölçek notu" className="h-8 flex-1 text-xs" data-testid={`guide-ing-measure-${i}`} />
                  <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => setIngs(ings.filter((_, k) => k !== i))} data-testid={`guide-ing-remove-${i}`}><X className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
              <div className="flex gap-2 pt-1">
                <Button type="button" size="sm" variant="secondary" className="h-7" onClick={() => setIngs([...ings, { name: "", amount: "", measure: "" }])} data-testid="guide-ing-add"><Plus className="mr-1 h-3.5 w-3.5" /> Satır ekle</Button>
                <Button type="button" size="sm" variant="ghost" className="h-7" onClick={() => setIngs(fromRecipe(recipe))} data-testid="guide-ing-reset"><RotateCcw className="mr-1 h-3.5 w-3.5" /> Reçeteden doldur</Button>
              </div>
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs">Adımlar (her satır bir adım)</Label>
          <Textarea data-testid="guide-steps-input" value={steps} onChange={(e) => setSteps(e.target.value)} rows={10} className="mt-1 font-mono text-sm"
            placeholder={"Blendera 300 ml süt ekleyin\n1 ölçek protein tozunu ekleyin\n30 saniye yüksek devirde karıştırın\nBuzlu bardağa servis edin"} />
          <p className="mt-1 text-xs text-muted-foreground">{steps.split("\n").filter((s) => s.trim()).length} adım</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <Label className="text-xs">Hazırlama Süresi (dk)</Label>
            <Input data-testid="guide-prep-input" type="number" min="0" value={prep} onChange={(e) => setPrep(e.target.value)} className="mt-1" placeholder="örn. 3" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">İpucu / Not (isteğe bağlı)</Label>
            <Textarea data-testid="guide-tip-input" value={tip} onChange={(e) => setTip(e.target.value)} rows={3} maxLength={1000} className="mt-1 text-sm" placeholder={"örn. Daha kremamsı için 2 küp buz ekleyin\nServis öncesi 1 dk bekletin"} />
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" data-testid="guide-cancel" onClick={onClose}>Vazgeç</Button>
        <Button data-testid="guide-save" onClick={save} disabled={saving} className="font-semibold">Kaydet</Button>
      </div>
    </div>
  );
};
